import { useForm, useWatch } from "react-hook-form"
import CountrySelect from "@/components/forms/country.select"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import StateSelect from "@/components/forms/state.select"
import { Input } from "@/components/ui/input"
import { useStates } from "@/api/data"
import { addressSchema } from "./address.schema"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { AsyncButton } from "@/components/ux/async.button"

interface CustomerAddressFormProps {
  defaultValues: z.infer<typeof addressSchema>;
  onSubmit: (data: z.infer<typeof addressSchema>) => void;
}

const CountryStateSelect = () => {
  const countryId = useWatch({ name: "countryId" })
  const { states } = useStates(countryId)

  return (
    <div className="flex flex-row gap-4">
      <FormField
        name="countryId"
        render={({ field }) => (
          <FormItem className="w-6/12">
            <FormLabel required>Country</FormLabel>
            <FormControl>
              <CountrySelect
                onChange={field.onChange}
                defaultValue={field.value}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {countryId !== "" && (
        <FormField
          name="stateId"
          render={({ field }) => (
            <FormItem className={states.length ? "w-6/12" : ""}>
              <FormLabel>State</FormLabel>
              <FormControl>
                <StateSelect
                  countryId={countryId}
                  onChange={field.onChange}
                  value={field.value}
                  defaultValue={field.value}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  )
}

export default function AddressForm({
  defaultValues,
  onSubmit,
}: CustomerAddressFormProps) {
  const form = useForm<z.infer<typeof addressSchema>>({
    resolver: zodResolver(addressSchema),
    defaultValues,
  })

  return (
    <Form {...form}>
      <form className="flex-grow flex flex-col gap-4">
        <CountryStateSelect />
        <FormField
          name="streetLine1"
          render={({ field }) => (
            <FormItem>
              <FormLabel required>Address line 1</FormLabel>
              <FormControl>
                <Input placeholder="Street and number" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          name="streetLine2"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Address line 2</FormLabel>
              <FormControl>
                <Input placeholder="Floor, apartment or entrance" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex flex-row gap-4">
          <FormField
            name="city"
            render={({ field }) => (
              <FormItem className="w-6/12">
                <FormLabel required>City</FormLabel>
                <FormControl>
                  <Input placeholder="City" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="postalCode"
            render={({ field }) => (
              <FormItem className="w-6/12">
                <FormLabel required>Zip/Postal Code</FormLabel>
                <FormControl>
                  <Input placeholder="Zip/Postal Code" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <AsyncButton type="button" onClick={form.handleSubmit(onSubmit)}>
          Save
        </AsyncButton>
      </form>
    </Form>
  )
}
